import { AbilityBuilder, PureAbility } from "@casl/ability";
import { createContextualCan } from "@casl/react";
import { CompanyType, UserRole } from "@prisma/client";
import { createContext } from "react";
import { match } from "ts-pattern";
import { type AuthenticatedUser } from "~/lib/session";
import { type NullableAuthenticatedUser } from "~/services/auth/fetchAuthenticatedUser";
import { type SubscriptionStatusWithPermissions } from "~/services/subscriptions/validateSubscription";
import { Action, Subject, type AbilityParams, type AppAbility } from "~/services/user/permissions/caslPermissionsType";
import { createPermissionsFromRolePermissions } from "~/services/user/permissions/utils/createPermissionsFromRolePermissions";
import { createPermissionsFromUserPermissions } from "~/services/user/permissions/utils/createPermissionsFromUserPermissions";

export const createAbility = (user: NullableAuthenticatedUser, params: AbilityParams) => {
  if (!user) {
    return createAnonymousAbility();
  }

  return createAbilityForUser(user, params);
};

export const createAnonymousAbility = () => {
  return new AbilityBuilder<AppAbility>(PureAbility<[Action, Subject]>).build();
};

export const createAbilityForUser = (user: AuthenticatedUser, params: AbilityParams) => {
  const roleRules = match(user.permissions.role)
    .with(UserRole.EMPLOYEE, () => defineEmployeeRules())
    .with(UserRole.RECRUITER, () => defineRecruiterRules())
    .with(UserRole.HR, () => defineHrRules(user))
    .with(UserRole.ADMIN, () => defineAdminRules())
    .exhaustive();

  const companyRolePermissions = createPermissionsFromRolePermissions({
    companyRolePermissions: params.rolePermissions,
    role: user.permissions.role,
    isManager: user.permissions.isManager,
  });

  const userPermissions = createPermissionsFromUserPermissions(user);

  const superAdminRules = user.isSuperAdmin ? defineSuperAdminRules(user) : [];

  const companyRules = defineCompanyRules(user, params);

  // The order of rule concatenation is EXTREMELY important here!
  // The last one in takes precedence over the first one, so we have to put the
  // biggest restriction in the end.
  return new PureAbility<[Action, Subject]>([
    ...roleRules,
    ...companyRolePermissions,
    ...userPermissions,
    ...superAdminRules,
    ...companyRules,
  ]);
};

const defineSuperAdminRules = (user: AuthenticatedUser) => {
  const { can, rules } = new AbilityBuilder<AppAbility>(PureAbility);

  if (user.isSuperAdmin) {
    can(Action.Update, Subject.Users);
    can(Action.Access, Subject.Account);
    can(Action.Access, Subject.Settings);
    can(Action.Access, Subject.Integrations);
    can(Action.Access, Subject.CompanyDashboard);
    can(Action.Access, Subject.CompensationReview);
    can(Action.Access, Subject.SalaryBands);
    can(Action.Access, Subject.ImportedEmployees);
    can(Action.Access, Subject.UserImpersonation);
  }

  return rules;
};

const defineCompanyRules = (user: AuthenticatedUser, params: AbilityParams) => {
  const { cannot, rules } = new AbilityBuilder<AppAbility>(PureAbility);

  if (!companyHasMarketDataAccess(params.subscriptions)) {
    cannot(Action.Access, Subject.MarketData);
    cannot(Action.Access, Subject.PeopleDashboard_MarketData);
  }

  if (!companyHasSalaryBandsAccess(params.subscriptions)) {
    cannot(Action.Access, Subject.PeopleDashboard_SalaryBands);
    cannot(Action.Access, Subject.SalaryBands);
  }

  if (!companyHasCompensationReviewAccess(params.subscriptions)) {
    cannot(Action.Access, Subject.CompensationReview);
  }

  if (!companyHasComptryxAccess(params.subscriptions)) {
    cannot(Action.Access, Subject.PeopleDashboard_Comptryx);
    cannot(Action.Access, Subject.MarketData_Comptryx);
  }

  if (user.company.type === CompanyType.PARTICIPANT && !user.company.liveSurveyId) {
    cannot(Action.Access, Subject.PeopleDashboard_MarketData);
    cannot(Action.Access, Subject.PeopleDashboard_SalaryBands);
    cannot(Action.Access, Subject.SalaryBands);
    cannot(Action.Access, Subject.CompanyDashboard);
    return rules;
  }

  if (user.company.type === CompanyType.VENTURE_CAPITAL) {
    cannot(Action.Access, Subject.RawData);
    cannot(Action.Access, Subject.Settings);
    cannot(Action.Access, Subject.Integrations);
    cannot(Action.Access, Subject.PeopleDashboard_MarketData);
    cannot(Action.Access, Subject.PeopleDashboard_SalaryBands);
    cannot(Action.Access, Subject.SalaryBands);

    if (!user.company.portfolioCompanies || user.company.portfolioCompanies.length === 0) {
      cannot(Action.Access, Subject.CompanyDashboard);
    }
  }

  return rules;
};

const defineRecruiterRules = () => {
  const { can, cannot, rules } = new AbilityBuilder<AppAbility>(PureAbility);

  can(Action.Access, Subject.MarketData); // to be changed
  can(Action.Access, Subject.MarketData_Comptryx); // to be changed
  can(Action.Access, Subject.CompensationReview); // to be changed
  can(Action.Access, Subject.SalaryBands); // to be changed

  cannot(Action.Access, Subject.PeopleDashboard_MarketData); // to be changed
  cannot(Action.Access, Subject.PeopleDashboard_SalaryBands); // to be changed
  cannot(Action.Access, Subject.PeopleDashboard_Comptryx); // to be changed
  cannot(Action.Update, Subject.Users);
  cannot(Action.Access, Subject.Account);
  cannot(Action.Access, Subject.RawData);
  cannot(Action.Access, Subject.Settings);
  cannot(Action.Access, Subject.Integrations);
  cannot(Action.Access, Subject.CompanyDashboard);
  cannot(Action.Access, Subject.ImportedEmployees);
  cannot(Action.Access, Subject.FileManager);
  cannot(Action.Access, Subject.UserImpersonation);

  return rules;
};

const defineHrRules = (user: AuthenticatedUser) => {
  const { can, cannot, rules } = new AbilityBuilder<AppAbility>(PureAbility);

  can(Action.Access, Subject.Account);
  can(Action.Access, Subject.RawData);
  can(Action.Access, Subject.MarketData);
  can(Action.Access, Subject.MarketData_Comptryx);
  can(Action.Access, Subject.PeopleDashboard_MarketData);
  can(Action.Access, Subject.PeopleDashboard_SalaryBands);
  can(Action.Access, Subject.PeopleDashboard_Comptryx);
  can(Action.Access, Subject.CompanyDashboard);
  can(Action.Access, Subject.CompensationReview);
  can(Action.Access, Subject.SalaryBands);
  can(Action.Access, Subject.ImportedEmployees);
  can(Action.Access, Subject.FileManager);
  can(Action.Access, Subject.UserImpersonation);

  cannot(Action.Update, Subject.Users);
  cannot(Action.Access, Subject.Settings);
  cannot(Action.Access, Subject.Integrations);

  if (!user.isCompensationReviewCampaignAdmin) {
    cannot(Action.Access, Subject.UserImpersonation);
  }

  return rules;
};

const defineEmployeeRules = () => {
  const { cannot, rules } = new AbilityBuilder<AppAbility>(PureAbility);

  cannot(Action.Access, Subject.CompensationReview);
  cannot(Action.Access, Subject.SalaryBands);
  cannot(Action.Access, Subject.MarketData);
  cannot(Action.Access, Subject.MarketData_Comptryx);
  cannot(Action.Update, Subject.Users);
  cannot(Action.Access, Subject.Account);
  cannot(Action.Access, Subject.RawData);
  cannot(Action.Access, Subject.Settings);
  cannot(Action.Access, Subject.Integrations);
  cannot(Action.Access, Subject.CompanyDashboard);
  cannot(Action.Access, Subject.ImportedEmployees);
  cannot(Action.Access, Subject.FileManager);
  cannot(Action.Access, Subject.UserImpersonation);
  cannot(Action.Access, Subject.PeopleDashboard_MarketData);
  cannot(Action.Access, Subject.PeopleDashboard_SalaryBands);
  cannot(Action.Access, Subject.PeopleDashboard_Comptryx);

  return rules;
};

const defineAdminRules = () => {
  const { can, rules } = new AbilityBuilder<AppAbility>(PureAbility);
  can(Action.Update, Subject.Users);
  can(Action.Access, Subject.Account);
  can(Action.Access, Subject.RawData);
  can(Action.Access, Subject.Settings);
  can(Action.Access, Subject.MarketData);
  can(Action.Access, Subject.MarketData_Comptryx);
  can(Action.Access, Subject.Integrations);
  can(Action.Access, Subject.PeopleDashboard_MarketData);
  can(Action.Access, Subject.PeopleDashboard_SalaryBands);
  can(Action.Access, Subject.PeopleDashboard_Comptryx);
  can(Action.Access, Subject.CompanyDashboard);
  can(Action.Access, Subject.CompensationReview);
  can(Action.Access, Subject.SalaryBands);
  can(Action.Access, Subject.ImportedEmployees);
  can(Action.Access, Subject.FileManager);
  can(Action.Access, Subject.GenderPayGapReport);
  can(Action.Access, Subject.UserImpersonation);

  return rules;
};

export const AbilityContext = createContext<AppAbility>(createAnonymousAbility());
export const Can = createContextualCan(AbilityContext.Consumer);

const companyHasMarketDataAccess = (subscriptions: SubscriptionStatusWithPermissions): boolean => {
  if (!subscriptions) {
    return false;
  }

  return subscriptions.CAN_ACCESS_BENCHMARK;
};

const companyHasSalaryBandsAccess = (subscriptions: SubscriptionStatusWithPermissions): boolean => {
  if (!subscriptions) {
    return false;
  }

  return subscriptions.CAN_ACCESS_SALARY_BANDS;
};

const companyHasCompensationReviewAccess = (subscriptions: SubscriptionStatusWithPermissions): boolean => {
  if (!subscriptions) {
    return false;
  }

  return subscriptions.CAN_ACCESS_COMPENSATION_REVIEW;
};

const companyHasComptryxAccess = (subscriptions: SubscriptionStatusWithPermissions): boolean => {
  if (!subscriptions) {
    return false;
  }

  return subscriptions.CAN_ACCESS_COMPTRYX;
};
